import { FALLBACK_LOCALE, MARKETS, findMarket, getMarketDefaultLanguage } from './markets';
import { formatLocale, isSupportedLocale } from './locales';

/**
 * Choose the locale for a visitor who arrived without one in the URL.
 *
 * Order of evidence, strongest first:
 *   1. a locale they previously chose themselves (an explicit switch outranks
 *      any guess we could make from their browser),
 *   2. their browser's language list, read as BCP-47 (`de-AT` -> `at-de`), first
 *      trying an exact market+language hit, then the language alone,
 *   3. the fallback locale.
 *
 * Returns a locale code that is always supported.
 */

function fromBcp47Tag(tag) {
    const [languagePart = '', regionPart = ''] = String(tag || '').toLowerCase().split('-');

    if (!/^[a-z]{2}$/u.test(languagePart)) {
        return null;
    }

    return {
        languageCode: languagePart,
        marketCode: /^[a-z]{2}$/u.test(regionPart) ? regionPart : null,
    };
}

function firstMarketSpeaking(languageCode) {
    const market = MARKETS.find((candidate) => candidate.languages.includes(languageCode));

    return market ? market.code : null;
}

export function negotiateLocale({ storedLocale, browserLanguages = [] } = {}) {
    if (isSupportedLocale(storedLocale)) {
        return storedLocale;
    }

    const parsedTags = browserLanguages.map(fromBcp47Tag).filter(Boolean);

    // Exact market + language, e.g. `de-AT` -> `at-de`.
    const exactMatch = parsedTags.find((tag) => (
        tag.marketCode && isSupportedLocale(formatLocale(tag.marketCode, tag.languageCode))
    ));

    if (exactMatch) {
        return formatLocale(exactMatch.marketCode, exactMatch.languageCode);
    }

    // Right country, language we do not serve there — keep the market, use its default.
    const marketMatch = parsedTags.find((tag) => tag.marketCode && findMarket(tag.marketCode));

    if (marketMatch) {
        return formatLocale(marketMatch.marketCode, getMarketDefaultLanguage(marketMatch.marketCode));
    }

    // Right language, no country we recognise — first market that speaks it.
    const languageMatch = parsedTags
        .map((tag) => ({ tag, marketCode: firstMarketSpeaking(tag.languageCode) }))
        .find((candidate) => candidate.marketCode);

    if (languageMatch) {
        return formatLocale(languageMatch.marketCode, languageMatch.tag.languageCode);
    }

    return FALLBACK_LOCALE;
}
